import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type {
  ColDef,
  IServerSideGetRowsParams,
  SortModelItem,
} from 'ag-grid-community';
import _ from 'lodash';

import aguid from 'lib/aguid';

import GridContext from '../../contexts/GridContext';
import captureException from '../../services/captureException';
import search from './search';
import BoardSlideOutContext from '../../contexts/BoardSlideOutContext';
import GqlClientContext from '../../contexts/GqlClientContext';
import AnalyticsContext from '../../contexts/AnalyticsContext';
import DatasetDefinitionsContext from '../../contexts/DatasetDefinitionsContext';
import useDateScope from '../useDateScope';
import getIdentifier from '../../getIdentifier';
import {
  CONTRACT_PROGRESS_CELL_FIELD,
  PROGRESS_CELL_TYPE,
} from '../../components/Grid/gridConstants';

const BONUS_EXPECTATION_CELL_TYPE = 'Bonus Expectation Field';

/**
 * "Bonus Expectation Field" columns render as `actual / target` (see
 * BonusExpectationCell). The column can be keyed on either the criteria's
 * actualField or targetField. When such a column is sorted, we sort by the
 * targetField first and then the actualField within each target group — the
 * same two-level behaviour as Progress cells (see toSortField) — so rows are
 * grouped by target and ordered by actual within each group. Returns the
 * matched field pair, or undefined for any non-expectation column.
 */
export const getExpectationSortFields = ({
  colId,
  colDef,
  bonusProgram,
}: {
  colId: string;
  colDef: ColDef | null | undefined;
  bonusProgram: BonusTypes.BonusProgram | undefined;
}): { actualField: string; targetField: string } | undefined => {
  if (!bonusProgram) {
    return undefined;
  }

  const fieldView = colDef?.cellRendererParams?.fieldView as
    | FleetOps.BaseViewField
    | undefined;
  if (fieldView?.cellType !== BONUS_EXPECTATION_CELL_TYPE) {
    return undefined;
  }

  const allFields = [
    ...bonusProgram.criteriaFields,
    ...(bonusProgram.qualifierFields ?? []),
    ...(bonusProgram.disqualifierFields ?? []),
  ];
  const match = allFields.find(
    (f) => f.actualField === colId || f.targetField === colId,
  );

  if (!match) {
    return undefined;
  }

  return { actualField: match.actualField, targetField: match.targetField };
};

// The search sort for a grid's sort model. Takes a colDef lookup rather than
// the grid api so DataTable's server-side tables share it.
export const toSortField = ({
  sortModelItems,
  getColDef,
  bonusProgram,
}: {
  sortModelItems: SortModelItem[];
  getColDef: (colId: string) => ColDef | null | undefined;
  bonusProgram: BonusTypes.BonusProgram | undefined;
}): SortField[] => {
  if (sortModelItems.length === 0) {
    return [];
  }

  const sortColDef = getColDef(sortModelItems[0].colId);

  const isSortingByProgress =
    !!sortColDef &&
    sortColDef.cellRendererParams &&
    sortColDef.cellRendererParams.cell &&
    sortColDef.cellRendererParams.cell.type === PROGRESS_CELL_TYPE;
  if (
    sortModelItems.length > 0 &&
    sortModelItems[0].colId === CONTRACT_PROGRESS_CELL_FIELD
  ) {
    return [
      {
        field: 'contractCommitment',
        sort: sortModelItems[0].sort as SortBy,
      },
      {
        field: 'commitment',
        sort: sortModelItems[0].sort as SortBy,
      },
    ];
  } else if (isSortingByProgress && sortColDef) {
    try {
      const cell = sortColDef.cellRendererParams.cell;
      const { totalField, targetField } = cell;
      return [
        {
          field: targetField,
          sort: sortModelItems[0].sort as SortBy,
        },
        {
          field: totalField,
          sort: sortModelItems[0].sort as SortBy,
        },
      ];
    } catch (ex) {
      captureException(ex);
      return [];
    }
  }

  return sortModelItems.flatMap((s) => {
    const colDef = getColDef(s.colId);
    const expectationFields = getExpectationSortFields({
      colId: s.colId,
      colDef,
      bonusProgram,
    });

    if (expectationFields) {
      return [
        { field: expectationFields.targetField, sort: s.sort },
        { field: expectationFields.actualField, sort: s.sort },
      ] as SortField[];
    }

    return [{ field: s.colId, sort: s.sort } as SortField];
  });
};

const useDatasource = (
  includeFields: string[] | undefined,
  dataType: string,
  filterInput: FilterInput[],
  rowEnricher?: (row: ElasticDocument) => ElasticDocument,
) => {
  const { client } = useContext(GqlClientContext);
  const { restartGrid, setIsLoadingData } = useContext(GridContext);
  const dateScope = useDateScope({});
  const driverBonusSlideOutContext = useContext(BoardSlideOutContext);
  const { trackEvent } = useContext(AnalyticsContext);
  const { getBonusProgram } = useContext(DatasetDefinitionsContext);
  const bonusProgram = useMemo(
    () => getBonusProgram(dataType),
    [getBonusProgram, dataType],
  );
  const [gridSessionId] = useState<string>(aguid());

  const trackInitialPage = useCallback(() => {
    trackEvent('Grid - Initial Page loaded', { dataType, gridSessionId });
  }, [dataType, gridSessionId, trackEvent]);
  const trackAdditionalPage = useCallback(() => {
    trackEvent('Grid - Additional Page loaded', { dataType, gridSessionId });
  }, [dataType, gridSessionId, trackEvent]);
  const trackFailedToLoadAdditionalPage = useCallback(() => {
    trackEvent('Grid - Failed to load additional page', {
      dataType,
      gridSessionId,
    });
  }, [dataType, gridSessionId, trackEvent]);

  const createDataSource = useCallback(() => {
    return {
      documents: [],
      hasMoreRows() {
        if (!this.searchResult) {
          return false;
        }
        return this.documents.length < this.searchResult.count;
      },
      lastRequestId: undefined,
      getRows(params: IServerSideGetRowsParams) {
        const { success, fail } = params;
        const requestId = getIdentifier();
        this.lastRequestId = requestId;

        if (
          this.searchResult &&
          _.isEqual(this.searchResult.sorting, params.request.sortModel)
        ) {
          // Get next page (using searchAfter)
          setIsLoadingData(true);
          search({
            dataType,
            sortBy: toSortField({
              sortModelItems: params.request.sortModel,
              getColDef: (colId) => params.api.getColumnDef(colId),
              bonusProgram,
            }),
            filters: filterInput,
            dateScope,
            client,
            searchAfter: this.searchResult.searchAfter,
            includeFields,
          })
            .then((result) => {
              if (requestId !== this.lastRequestId) {
                return;
              }
              if (this.searchResult) {
                this.searchResult = {
                  ...this.searchResult,
                  searchAfter: result.searchAfter,
                };
              }

              this.documents = [...this.documents, ...result.documents];
              trackAdditionalPage();
              success({
                rowData: rowEnricher
                  ? result.documents.map(rowEnricher)
                  : result.documents,
                rowCount: this.hasMoreRows()
                  ? this.documents.length + 1
                  : this.documents.length,
              });
              setIsLoadingData(false);
            })
            .catch((ex) => {
              captureException(ex);
              fail();
              trackFailedToLoadAdditionalPage();
              restartGrid();
            });
        } else {
          // Clear searchResult
          this.searchResult = undefined;
          setIsLoadingData(true);
          // Get first page
          search({
            dataType,
            sortBy: toSortField({
              sortModelItems: params.request.sortModel,
              getColDef: (colId) => params.api.getColumnDef(colId),
              bonusProgram,
            }),
            filters: filterInput,
            client,
            dateScope,
            includeFields,
          })
            .then((result) => {
              if (requestId !== this.lastRequestId) {
                return;
              }
              trackInitialPage();
              this.documents = result.documents;
              this.searchResult = {
                count: result.count,
                searchAfter: result.searchAfter,
                sorting: params.request.sortModel,
              };
              success({
                rowData: rowEnricher
                  ? result.documents.map(rowEnricher)
                  : result.documents,
                rowCount: this.hasMoreRows()
                  ? this.documents.length + 1
                  : this.documents.length,
              });
              setIsLoadingData(false);
            })
            .catch((ex) => {
              captureException(ex);
              fail();
            });
        }
      },
    } as DataSource;
  }, [
    bonusProgram,
    client,
    dataType,
    dateScope,
    filterInput,
    includeFields,
    restartGrid,
    rowEnricher,
    setIsLoadingData,
    trackAdditionalPage,
    trackFailedToLoadAdditionalPage,
    trackInitialPage,
  ]);

  const [dataSource, setDataSource] = useState<DataSource | undefined>();

  useEffect(() => {
    setDataSource(createDataSource());
  }, [createDataSource]);

  useEffect(() => {
    if (
      driverBonusSlideOutContext &&
      driverBonusSlideOutContext.gridRequiresRefresh
    ) {
      setDataSource(createDataSource());
      driverBonusSlideOutContext.setGridRequiresRefresh(false);
    }
  }, [driverBonusSlideOutContext, createDataSource]);

  return {
    dataSource,
  };
};

export default useDatasource;
